import { useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  AlertTriangle,
  Braces,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  Info,
  Loader2,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputDropdown } from '@/components/ui/input-dropdown';
import { Select } from '@/components/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useNodeStore } from '@/stores/node-store';
import type { CustomNode, InputValue, NodeStruct } from '@/types/node-types';
import type { NodeStatusEvent } from '@/types/workflow-types';
import {
  getRepeatPseudoCycleConfig,
  REPEAT_MAX_ITERATIONS_HARD,
} from '@/pages/workflow/helper/repeat-control';
import {
  checkLoopRules,
  nodeLabel,
  type LoopRuleProblem,
} from '@/pages/workflow/helper/repeat-loop';
import {
  JUDGE_ROOT,
  configFromSentence,
  describeSentence,
  entryCandidates,
  feedbackSlotsFor,
  judgeCandidates,
  judgeOutputSuggestions,
  presetsForJudge,
  sentenceFromGraph,
  type LoopSentence,
} from '@/pages/workflow/helper/repeat-author';
import {
  readLoopRun,
  type TimelineEntry,
} from '@/pages/workflow/helper/repeat-run';
import {
  describeCondition,
  formatFieldPath,
  newRule,
  parseFieldPath,
  type Condition,
} from '@/pages/workflow/helper/switch-condition-builder';
import { ConditionEditor } from './switch-cases-editor';
import { JsonEditorField } from './json-editor-field';
import { cn } from '@/lib/utils';

interface RepeatUntilPanelProps {
  nodeId: string;
  disabled?: boolean;
}

const struct = (node: CustomNode | undefined): NodeStruct | undefined =>
  node?.data?.struct;

const selectClass = 'h-8 w-auto min-w-0 text-xs px-2';

/** The config panel for a Repeat Until node: one sentence with pickers,
 * the compiler's rules checked live, and the run explained. */
export function RepeatUntilPanel({
  nodeId,
  disabled = false,
}: RepeatUntilPanelProps) {
  const {
    nodes,
    edges,
    groupedNodes,
    applyLoopSentence,
    handleParameterChange,
  } = useNodeStore(
    useShallow((state) => ({
      nodes: state.nodes,
      edges: state.edges,
      groupedNodes: state.groupedNodes,
      applyLoopSentence: state.applyLoopSentence,
      handleParameterChange: state.handleParameterChange,
    })),
  );
  const catalog = useMemo(
    () => Object.values(groupedNodes).flat(),
    [groupedNodes],
  );
  const carrier = nodes.find((node) => node.id === nodeId);
  const { sentence, loop } = useMemo(
    () => sentenceFromGraph(nodes, edges, nodeId, catalog),
    [nodes, edges, nodeId, catalog],
  );
  const config = getRepeatPseudoCycleConfig(struct(carrier));
  const problems = useMemo(
    () => (loop ? checkLoopRules(nodes, edges, loop, catalog) : []),
    [nodes, edges, loop, catalog],
  );
  const ceiling = Math.min(
    struct(carrier)?.repeat_runtime_limits?.max_iterations ??
      catalog.find((entry) => entry.node_id === 'repeat_until_node')
        ?.repeat_runtime_limits?.max_iterations ??
      REPEAT_MAX_ITERATIONS_HARD,
    REPEAT_MAX_ITERATIONS_HARD,
  );
  const entry = sentence.entryId
    ? nodes.find((node) => node.id === sentence.entryId)
    : undefined;
  const judge = sentence.judgeId
    ? nodes.find((node) => node.id === sentence.judgeId)
    : undefined;
  const entryOptions = useMemo(
    () => entryCandidates(nodes, nodeId, catalog),
    [nodes, nodeId, catalog],
  );
  const judgeOptions = useMemo(
    () => judgeCandidates(nodes, edges, nodeId, sentence.entryId, catalog),
    [nodes, edges, nodeId, sentence.entryId, catalog],
  );
  const suggestions = useMemo(() => judgeOutputSuggestions(judge), [judge]);
  const presets = useMemo(() => presetsForJudge(struct(judge)), [judge]);
  const feedbackSlots = feedbackSlotsFor(struct(entry), catalog);
  const [showJson, setShowJson] = useState(false);

  const nodeNames = useMemo(
    () => Object.fromEntries(nodes.map((node) => [node.id, nodeLabel(node)])),
    [nodes],
  );
  const run = useMemo(
    () =>
      readLoopRun({
        result: carrier?.data?.result,
        events: carrier?.data?.nodeStatusEvents as
          | NodeStatusEvent[]
          | undefined,
        maxIterations: sentence.maxIterations,
        nodeNames,
        evaluatorId: sentence.judgeId,
        entryId: sentence.entryId,
      }),
    [
      carrier?.data?.result,
      carrier?.data?.nodeStatusEvents,
      sentence,
      nodeNames,
    ],
  );

  const apply = useCallback(
    (patch: Partial<LoopSentence>) => {
      applyLoopSentence(nodeId, { ...sentence, ...patch });
    },
    [applyLoopSentence, nodeId, sentence],
  );

  if (!carrier) return null;

  const entryLabel = entry ? nodeLabel(entry) : '';
  const judgeLabel = judge ? nodeLabel(judge) : '';
  const errors = problems.filter((problem) => problem.severity === 'error');
  const ready = errors.length === 0 && sentence.entryId && sentence.judgeId;

  return (
    <div
      className='space-y-4 px-3 py-2 text-sm'
      data-testid='repeat-until-panel'
    >
      {/* The sentence */}
      <section
        className='space-y-3 rounded-md border bg-muted/20 p-3'
        data-testid='repeat-sentence'
      >
        <div className='flex items-center gap-1 text-xs font-semibold text-muted-foreground'>
          <RotateCcw className='size-3.5' />
          The loop, in one sentence
        </div>

        <div className='flex flex-wrap items-center gap-x-1.5 gap-y-2 text-sm leading-7'>
          <span>Repeat</span>
          <span data-testid='repeat-pick-entry' className='inline-flex'>
            <Select
              options={entryOptions.map((option) => option.label)}
              selected={entryLabel || undefined}
              placeholder='pick the step…'
              allowAllOption={false}
              disabled={disabled}
              className={cn(
                selectClass,
                'min-w-[150px] max-w-[220px]',
                !entryLabel && 'border-amber-400',
              )}
              onSelect={(label) => {
                const picked = entryOptions.find(
                  (option) => option.label === label,
                );
                if (!picked) return;
                const pickedNode = nodes.find((node) => node.id === picked.id);
                const slots = feedbackSlotsFor(struct(pickedNode), catalog);
                apply({
                  entryId: picked.id,
                  feedbackSlot: slots[0] ?? null,
                  // A judge that is no longer downstream of the new step is dropped.
                  judgeId: judgeCandidates(
                    nodes,
                    edges,
                    nodeId,
                    picked.id,
                    catalog,
                  ).some((option) => option.id === sentence.judgeId)
                    ? sentence.judgeId
                    : null,
                });
              }}
            />
          </span>
          <span>until</span>
          <span data-testid='repeat-pick-judge' className='inline-flex'>
            <Select
              options={judgeOptions.map((option) => option.label)}
              selected={judgeLabel || undefined}
              placeholder='pick the judge…'
              allowAllOption={false}
              disabled={disabled || judgeOptions.length === 0}
              className={cn(
                selectClass,
                'min-w-[150px] max-w-[220px]',
                !judgeLabel && 'border-amber-400',
              )}
              onSelect={(label) => {
                const picked = judgeOptions.find(
                  (option) => option.label === label,
                );
                if (!picked) return;
                const pickedNode = nodes.find((node) => node.id === picked.id);
                const preset = presetsForJudge(struct(pickedNode))[0];
                apply({
                  judgeId: picked.id,
                  ...(preset
                    ? {
                        condition: preset.condition,
                        feedbackPath: preset.feedbackPath,
                      }
                    : {}),
                });
              }}
            />
          </span>
          <span>says</span>
          <span
            className='rounded bg-background px-1.5 py-0.5 text-xs font-medium'
            data-testid='repeat-condition-summary'
          >
            {describeCondition(sentence.condition)}
          </span>
          <span className='inline-flex items-center gap-1.5 whitespace-nowrap'>
            , at most
            <Input
              type='number'
              min={1}
              max={ceiling}
              value={sentence.maxIterations}
              disabled={disabled}
              aria-label='At most this many attempts'
              data-testid='repeat-max-iterations'
              className='h-8 w-16 text-xs'
              onChange={(event) => {
                const next = Number(event.target.value);
                if (Number.isFinite(next)) apply({ maxIterations: next });
              }}
            />
            times,
          </span>
          <span>passing</span>
          <InputDropdown
            key={formatFieldPath(sentence.feedbackPath)}
            value={
              sentence.feedbackPath.length
                ? formatFieldPath(sentence.feedbackPath)
                : ''
            }
            options={suggestions.map((path) => ({ value: path, label: path }))}
            placeholder={judge ? 'judge output' : 'pick a judge first'}
            aria-label='Judge output to pass back'
            data-testid='repeat-feedback-path'
            disabled={disabled || !judge}
            className='h-8 text-xs min-w-[120px] max-w-[180px]'
            onChange={() => undefined}
            onSelect={(option) =>
              apply({ feedbackPath: parseFieldPath(option.value) })
            }
            onBlur={(event) => {
              const parsed = parseFieldPath(
                (event.target as HTMLInputElement).value,
              );
              if (
                formatFieldPath(parsed) !==
                formatFieldPath(sentence.feedbackPath)
              ) {
                apply({ feedbackPath: parsed });
              }
            }}
          />
          <span>back into</span>
          {feedbackSlots.length > 1 ? (
            <span data-testid='repeat-feedback-slot' className='inline-flex'>
              <Select
                options={feedbackSlots.map((slot) => slot.target_id)}
                selected={sentence.feedbackSlot?.target_id}
                placeholder='pick an input…'
                allowAllOption={false}
                disabled={disabled}
                className={cn(selectClass, 'min-w-[120px]')}
                onSelect={(targetId) => {
                  const slot = feedbackSlots.find(
                    (candidate) => candidate.target_id === targetId,
                  );
                  if (slot) apply({ feedbackSlot: slot });
                }}
              />
            </span>
          ) : (
            <span
              className='rounded bg-background px-1.5 py-0.5 text-xs font-medium'
              data-testid='repeat-feedback-slot'
            >
              {sentence.feedbackSlot
                ? `${entryLabel ? `${entryLabel} → ` : ''}${sentence.feedbackSlot.target_id}`
                : entry
                  ? 'this step has no feedback input'
                  : '…'}
            </span>
          )}
          <span>.</span>
        </div>

        <p className='text-[11px] text-muted-foreground'>
          Attempts are capped at {ceiling} on this platform. The first attempt
          uses the step&apos;s own inputs; every later attempt receives the
          judge&apos;s output in the feedback input instead.
        </p>
      </section>

      {/* The condition, spelled out */}
      <section className='space-y-2' data-testid='repeat-condition'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-1 text-xs font-semibold text-muted-foreground'>
            Stop when {judgeLabel || 'the judge'} says
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className='size-3.5 cursor-help' />
              </TooltipTrigger>
              <TooltipContent>
                <p className='max-w-xs'>
                  Fields are read from the judge&apos;s output after each
                  attempt. For a Validate judge they are <code>is_valid</code>,{' '}
                  <code>errors</code> and <code>value</code>; for Classify,{' '}
                  <code>decision</code>.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-2 text-xs'
            data-testid='repeat-toggle-json'
            onClick={() => setShowJson((value) => !value)}
          >
            <Braces className='size-3' />{' '}
            {showJson ? 'Hide JSON' : 'Edit as JSON'}
          </Button>
        </div>
        {presets.length > 0 && !disabled && (
          <div className='flex flex-wrap gap-1'>
            {presets.map((preset) => (
              <Button
                key={preset.id}
                type='button'
                variant='outline'
                size='sm'
                className='h-6 px-2 text-xs'
                data-testid={`repeat-preset-${preset.id}`}
                onClick={() =>
                  apply({
                    condition: preset.condition,
                    feedbackPath: preset.feedbackPath,
                  })
                }
              >
                {preset.label}
              </Button>
            ))}
          </div>
        )}
        <ConditionEditor
          condition={sentence.condition}
          suggestions={suggestions}
          disabled={disabled}
          depth={0}
          root={JUDGE_ROOT}
          onChange={(condition: Condition) => apply({ condition })}
        />
        {sentence.condition.kind === 'always' && !disabled && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-6 px-2 text-xs'
            onClick={() => apply({ condition: newRule(['is_valid']) })}
          >
            Add a condition
          </Button>
        )}
        {showJson && (
          <div className='space-y-1'>
            <p className='text-[11px] text-muted-foreground'>
              The authored <code>repeat_config</code> exactly as it is saved.
              The server adds the compiled region at run time.
            </p>
            <JsonEditorField
              value={config ?? configFromSentence(sentence, null, nodeId)}
              rows={12}
              expectedShape='object'
              disabled={disabled}
              aria-label='repeat_config JSON'
              data-testid='repeat-config-json'
              onCommit={(parsed) =>
                handleParameterChange(
                  nodeId,
                  'repeat_config',
                  parsed as InputValue,
                )
              }
            />
          </div>
        )}
      </section>

      {/* Live rule check */}
      <section className='space-y-1.5' data-testid='repeat-rules'>
        {ready ? (
          <div className='flex items-center gap-1.5 rounded-md border border-emerald-200 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 px-2 py-1.5 text-xs text-emerald-900 dark:text-emerald-100'>
            <CheckCircle2 className='size-3.5' />
            {describeSentence(sentence, nodes)}
          </div>
        ) : null}
        {problems.map((problem) => (
          <RuleProblem
            key={`${problem.code}:${problem.nodeIds.join(',')}:${problem.message}`}
            problem={problem}
          />
        ))}
      </section>

      {/* The run, explained */}
      {(run.result || run.progress) && (
        <RunTimeline
          timeline={run.timeline}
          outcome={run.outcome}
          entryName={entryLabel}
          judgeName={judgeLabel}
        />
      )}
    </div>
  );
}

function RuleProblem({ problem }: { problem: LoopRuleProblem }) {
  const isError = problem.severity === 'error';
  return (
    <div
      className={cn(
        'rounded-md border px-2 py-1.5 text-xs',
        isError
          ? 'border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 text-red-900 dark:text-red-100'
          : 'border-amber-200 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-100',
      )}
      data-testid={`repeat-problem-${problem.code}`}
    >
      <div className='flex items-start gap-1.5'>
        <AlertTriangle className='mt-0.5 size-3.5 shrink-0' />
        <div>
          <div>{problem.message}</div>
          {problem.hint && (
            <div className='mt-0.5 text-[11px] opacity-80'>{problem.hint}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function RunTimeline({
  timeline,
  outcome,
  entryName,
  judgeName,
}: {
  timeline: TimelineEntry[];
  outcome: ReturnType<typeof readLoopRun>['outcome'];
  entryName: string;
  judgeName: string;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const toneClass = {
    success:
      'border-emerald-200 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-100',
    warning:
      'border-amber-200 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-100',
    error:
      'border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 text-red-900 dark:text-red-100',
    running:
      'border-sky-200 dark:border-sky-500/40 bg-sky-50 dark:bg-sky-500/10 text-sky-900 dark:text-sky-100',
    idle: 'border bg-muted/30 text-muted-foreground',
  }[outcome.tone];
  return (
    <section className='space-y-2' data-testid='repeat-run'>
      <div className='text-xs font-semibold text-muted-foreground'>The run</div>
      <div
        className={cn(
          'rounded-md border px-2 py-1.5 text-xs font-medium',
          toneClass,
        )}
        data-testid='repeat-outcome'
      >
        {outcome.tone === 'running' && (
          <Loader2 className='mr-1 inline size-3.5 animate-spin' />
        )}
        {outcome.sentence}
        {outcome.tone !== 'idle' && (
          <span className='ml-2 font-normal opacity-80'>
            ({outcome.iteration} of {outcome.maxIterations} attempts)
          </span>
        )}
      </div>
      <ol className='space-y-1' data-testid='repeat-timeline'>
        {timeline.map((entry) => {
          const isOpen = open === entry.iteration;
          const icon =
            entry.status === 'failed' ? (
              <XCircle className='size-3.5 text-red-600 dark:text-red-400' />
            ) : entry.status === 'running' ? (
              <Loader2 className='size-3.5 animate-spin text-sky-600 dark:text-sky-400' />
            ) : entry.satisfied ? (
              <CheckCircle2 className='size-3.5 text-emerald-600 dark:text-emerald-400' />
            ) : (
              <CircleDashed className='size-3.5 text-amber-600 dark:text-amber-400' />
            );
          return (
            <li
              key={entry.iteration}
              className='rounded-md border bg-background text-xs'
              data-testid={`repeat-iteration-${entry.iteration}`}
            >
              <button
                type='button'
                className='flex w-full items-center gap-2 px-2 py-1.5 text-left'
                onClick={() => setOpen(isOpen ? null : entry.iteration)}
              >
                {isOpen ? (
                  <ChevronDown className='size-3' />
                ) : (
                  <ChevronRight className='size-3' />
                )}
                {icon}
                <span className='whitespace-nowrap font-medium'>
                  Attempt {entry.iteration}
                </span>
                <span className='text-muted-foreground'>
                  {entry.status === 'failed'
                    ? `failed${entry.failedIn ? ` in ${entry.failedIn}` : ''}`
                    : entry.status === 'running'
                      ? 'running'
                      : `${judgeName || 'judge'}: ${entry.judge.verdict}`}
                </span>
                {entry.feedback && (
                  <span className='ml-auto rounded bg-amber-50 dark:bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-800 dark:text-amber-200'>
                    with feedback
                  </span>
                )}
              </button>
              {isOpen && (
                <div className='space-y-1.5 border-t px-2 py-2'>
                  {entry.judge.details.length > 0 && (
                    <ul className='list-disc pl-4 text-[11px]'>
                      {entry.judge.details.map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                  )}
                  {entry.feedback && (
                    <Detail
                      label={`Fed back into ${entryName || 'the step'}`}
                      value={entry.feedback}
                    />
                  )}
                  {Object.keys(entry.regionOutputs).length > 0 && (
                    <Detail
                      label='Outputs this attempt'
                      value={entry.regionOutputs}
                    />
                  )}
                  {entry.judgeValue !== undefined && (
                    <Detail
                      label={`${judgeName || 'Judge'} said`}
                      value={entry.judgeValue}
                    />
                  )}
                  {entry.errorCode && (
                    <div className='text-[11px] text-red-700 dark:text-red-300'>
                      Error: {entry.errorCode.replace(/_/g, ' ')}
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Detail({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <div className='text-[11px] font-medium text-muted-foreground'>
        {label}
      </div>
      <pre className='max-h-40 overflow-auto rounded bg-muted/40 p-1.5 text-[10px] leading-4'>
        {JSON.stringify(value, null, 1)}
      </pre>
    </div>
  );
}
